import { FileCategoryGroupEnum } from 'generated/graphql';
import type { DirectoryProduct } from '@modules/product/type/directory-product';

/**
 * The ArchiPro-generated spec sheet (tear sheet). It is not a real, supplier
 * editable `LibraryDownload` — it links to the on-the-fly `/tearsheet.pdf`
 * resource route. We inject it server-side (in the product loader) as the first
 * item of the supplier's "Spec Sheets" group so professionals can't delete it.
 */
type ProductDownloads = DirectoryProduct['LibraryDownloads'];

/** Sentinel ID used to recognise the generated sheet in the download list. */
export const ARCHIPRO_SPEC_SHEET_ID = -1;

/** Documenting party shown for the generated sheet (not the supplier). */
export const ARCHIPRO_SPEC_SHEET_PROFESSIONAL = 'ArchiPro';

/**
 * Fallback byte size for the generated PDF. The real size varies per product —
 * it is dominated by the embedded hero image — and we don't render the PDF when
 * building the download list, so we surface a representative figure rather than
 * a dash. Update this if the typical sheet weight shifts.
 */
export const ARCHIPRO_SPEC_SHEET_FALLBACK_BYTES = 950_000;

// The global "Spec Sheets" FileCategory (ID 6, SortOrder 5 — between Catalogues
// #5/#4 and Product Care Guide #7/#6). Sharing the real ID means
// `groupDownloadsByCategory` merges the generated sheet into the supplier's
// Spec Sheets group when one exists; otherwise it forms that group on its own.
const SPEC_SHEETS_CATEGORY_ID = 6;
const SPEC_SHEETS_CATEGORY_SORT_ORDER = 5;

/**
 * Prepends the ArchiPro-generated spec sheet to a product's downloads so it is
 * always the first item of the "Spec Sheets" group.
 *
 * Grouped `Specs`, the same as the supplier's own sheets beside it: it gates on
 * sign-in and creates the supplier a lead when it is taken. It was `Safety`
 * — ungated, on the reasoning that ArchiPro authored it so no lead was earned —
 * which left one row in the list behaving unlike every other row around it.
 *
 * `cachedSizeBytes` is the real PDF size recorded after a prior render (from
 * `Product.TearSheetSizeBytes`); when absent we show the representative fallback
 * and the page warms the real size in the background for next time.
 */
export const withArchiproSpecSheet = (
    downloads: ProductDownloads,
    productLink: string,
    cachedSizeBytes?: number | null,
): ProductDownloads => {
    const specSheet: ProductDownloads[number] = {
        __typename: 'LibraryDownload',
        ID: ARCHIPRO_SPEC_SHEET_ID,
        Title: 'Product Specs',
        ExternalLink: `${productLink}/tearsheet.pdf`,
        InternalLink: null,
        FileSizeBytes: cachedSizeBytes ?? ARCHIPRO_SPEC_SHEET_FALLBACK_BYTES,
        MemberSortOrder: -1,
        FileCategory: {
            __typename: 'FileCategory',
            ID: SPEC_SHEETS_CATEGORY_ID,
            Title: 'Spec Sheets',
            SortOrder: SPEC_SHEETS_CATEGORY_SORT_ORDER,
            Group: FileCategoryGroupEnum.Specs,
        },
    };

    return [specSheet, ...downloads];
};

/** Sentinel ID for the product's look book row, beside the tear sheet. */
export const ARCHIPRO_LOOK_BOOK_ID = -2;

/** The sheet's own route, which the book's is derived from. */
const TEAR_SHEET_ROUTE = /tearsheet\.pdf$/;

/**
 * The product's look book as a download row, made from the tear sheet's: the
 * same category and gate, since both are ArchiPro's own documents about the
 * product, with the book's route in place of the sheet's. No size: the book is
 * rendered per request and its weight varies too much with the photographs to
 * quote one.
 *
 * `null` where the sheet's link is not the route this rewrites — a query
 * string on the end, a CDN rewrite, a sheet that is an `InternalLink`. A
 * substitution that quietly does nothing would leave a row labelled Look Book
 * handing back the tear sheet, and a wrong file is worse than a missing row.
 */
export const archiproLookBookFrom = (
    specSheet: ProductDownloads[number],
): ProductDownloads[number] | null => {
    const sheetLink = specSheet.ExternalLink ?? '';
    if (!TEAR_SHEET_ROUTE.test(sheetLink)) return null;
    return {
        ...specSheet,
        ID: ARCHIPRO_LOOK_BOOK_ID,
        Title: 'Look Book',
        ExternalLink: sheetLink.replace(TEAR_SHEET_ROUTE, 'lookbook.pdf'),
        FileSizeBytes: null,
        MemberSortOrder: -1,
    };
};
